import { Component, DoCheck, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Ability, subject } from '@casl/ability';
import { from, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, mergeMap, takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../../../core/projects.service';
import {
  ProjectReferenceLayerService,
  ReferenceLayerSummary,
} from '../../../../../../../../../shared/services/project-reference-layer.service';
import {
  ProjectMapSourceFormat, ProjectMapSourceService, ProjectMapSourceSummary, ProjectMapSourceVersion, ProjectMapSourceListChange,
} from '../../../../../../../../../shared/services/project-map-source.service';
import { ProjectDrawingsService } from '../../../../../../../../../shared/services/project-drawings.service';
import { ProjectMapManifest, PublishedMapLayer } from '../../../../../../../../../shared/services/project-map-manifest.service';
import { ProjectMapPublishingService } from '../../../../../../../../../shared/services/project-map-publishing.service';
import { Project } from '../../../../../../../../../shared/models';
import { AuthService } from '../../../../../../../../../shared/services/auth.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { MapLayerGroupDialogComponent } from '../map-layer-group-dialog/map-layer-group-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../../../../../../../shared/components';
import { CreateOrEditMapLayerComponent } from '../create-or-edit-map-layer/create-or-edit-map-layer.component';

interface ManagedMapLayer {
  id: string;
  name: string;
  published?: PublishedMapLayer;
  reference?: ReferenceLayerSummary;
}

@Component({
  selector: 'app-map-layers-list',
  templateUrl: './map-layers-list.component.html',
  styleUrls: ['./map-layers-list.component.scss'],
  standalone: false,
})
export class MapLayersListComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input() project: Project;

  projectId: string;
  activeTab: 'map' | 'review' = 'map';
  /** Source to select on the Pending tab once the next import reload includes it. */
  private focusImportId: string | null = null;
  catalog: ProjectMapManifest | null = null;
  catalogLoading = false;
  catalogError = '';
  folderName = '';
  editingFolderId: string | null = null;
  // Layer chosen from a row's "Move to group → New group…" item; moved into the group on save.
  folderLayer: ManagedMapLayer | null = null;
  folderError = '';
  /** Register as drawing: the source being copied into a draft set, and why it failed */
  registeringSourceId: string | null = null;
  registerError = '';
  savingFolder = false;
  collapsedFolders = new Set<string>();
  sourceVersions: Record<string, ProjectMapSourceVersion> = {};
  private catalogAbort = new AbortController();
  private mutationAbort = new AbortController();
  private versionRequest?: Subscription;
  displayedColumns = ['name', 'source', 'features', 'actions'];
  rows: ReferenceLayerSummary[] = [];
  selectedRow: ReferenceLayerSummary | null = null;
  loading = false;
  importRows: ProjectMapSourceSummary[] = [];
  importColumns = ['importName', 'importSource', 'importStatus', 'importActions'];
  importsLoading = false;
  importError: string | null = null;
  importSearch = '';
  nextImportOffset: number | null = null;
  selectedImportId: string | null = null;
  private readonly importSearchChanged = new Subject<{ projectId: string; generation: number; search: string }>();
  private projectGeneration = 0;
  private referenceRequest?: Subscription;
  private importsRequest?: Subscription;
  private importGeneration = 0;
  private initialized = false;
  private accountId: string | undefined;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private refLayerService: ProjectReferenceLayerService,
    public sourceService: ProjectMapSourceService,
    public publishingService: ProjectMapPublishingService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private drawingsApi: ProjectDrawingsService,
    private ability: Ability,
  ) {}

  ngOnInit(): void {
    this.initialized = true;
    this.syncAccount();
    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      if (qp.subTab === 'overlays' || qp.subTab === 'map-layers') {
        this.resolveProjectId();
        if (this.projectId) this.refresh();
      }
    });

    this.refLayerService.listChanged
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => { if (this.projectId) this.refresh(); });

    this.sourceService.listChanged.pipe(takeUntil(this.onDestroy))
      .subscribe(change => {
        if (!this.projectId) return;
        // A freshly staged upload lands on the Pending tab, selected, so the
        // reader is not left on an empty On the map tab while it processes.
        const focus = change as ProjectMapSourceListChange | undefined;
        if (focus?.focus && focus.sourceId) this.focusImportId = focus.sourceId;
        this.refresh();
      });

    this.importSearchChanged.pipe(
      debounceTime(300),
      distinctUntilChanged((previous, current) => previous.projectId === current.projectId
        && previous.generation === current.generation && previous.search === current.search),
      takeUntil(this.onDestroy),
    ).subscribe(query => {
      if (query.projectId === this.projectId && query.generation === this.projectGeneration) this.refreshImports();
    });

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => { this.selectedRow = null; this.selectedImportId = null; });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes.project) {
      this.resolveProjectId();
      if (this.projectId) this.refresh();
    }
  }

  ngDoCheck(): void { this.syncAccount(); }

  private clearScope(): void {
    this.catalogAbort.abort(); this.mutationAbort.abort(); this.versionRequest?.unsubscribe();
    this.catalogAbort = new AbortController(); this.mutationAbort = new AbortController();
    this.catalog = null; this.sourceVersions = {}; this.catalogLoading = false; this.catalogError = '';
    this.folderName = ''; this.folderError = ''; this.savingFolder = false;
    this.editingFolderId = null; this.folderLayer = null; this.collapsedFolders.clear(); this.activeTab = 'map'; this.focusImportId = null;
    this.projectGeneration++; this.importGeneration++;
    this.referenceRequest?.unsubscribe(); this.importsRequest?.unsubscribe();
    this.rows = []; this.importRows = []; this.selectedRow = null; this.selectedImportId = null;
    this.loading = false; this.importsLoading = false; this.importSearch = ''; this.nextImportOffset = null; this.importError = null;
  }

  private syncAccount(): boolean {
    const accountId = this.authService.getCurrentUser()?.id || '';
    if (this.accountId !== undefined && this.accountId !== accountId) {
      this.clearScope(); this.projectsService.closeRightPanel.next();
    }
    this.accountId = accountId;
    return !!accountId;
  }

  private currentScope(projectId: string, generation: number, accountId: string): boolean {
    return this.syncAccount() && projectId === this.projectId && generation === this.projectGeneration && accountId === this.accountId;
  }

  ngOnDestroy(): void {
    this.catalogAbort.abort(); this.mutationAbort.abort(); this.versionRequest?.unsubscribe();
    this.referenceRequest?.unsubscribe();
    this.importsRequest?.unsubscribe();
    this.importSearchChanged.complete();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private resolveProjectId(): void {
    const segments = this.router.url.split('?')[0].split('/');
    const projectId = this.project?.id || this.route.snapshot.paramMap.get('projectId')
      || segments[segments.length - 1];
    if (projectId !== this.projectId) {
      this.clearScope();
    }
    this.projectId = projectId;
  }

  refresh(): void {
    if (!this.syncAccount() || !this.projectId) return;
    void this.refreshCatalog();
    const generation = this.projectGeneration;
    const accountId = this.accountId;
    this.referenceRequest?.unsubscribe();
    this.loading = true;
    const projectId = this.projectId;
    this.referenceRequest = this.refLayerService.list(projectId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: rows => {
          if (this.currentScope(projectId, generation, accountId)) { this.rows = rows; this.loading = false; }
        },
        error: error => {
          if (!this.currentScope(projectId, generation, accountId)) return;
          this.loading = false;
          if ([401, 403].includes(error?.status)) { this.clearScope(); this.projectsService.closeRightPanel.next(); }
        },
      });
    if (this.currentScope(projectId, generation, accountId)) this.refreshImports();
  }

  onImportSearchChange(): void {
    this.importSearchChanged.next({
      projectId: this.projectId, generation: this.projectGeneration, search: this.importSearch.trim(),
    });
  }

  refreshImports(append = false): void {
    if (!this.syncAccount()) return;
    const projectId = this.projectId;
    const scopeGeneration = this.projectGeneration;
    const accountId = this.accountId;
    if (!this.projectId || (append && this.nextImportOffset === null)) return;
    this.importsRequest?.unsubscribe();
    const generation = ++this.importGeneration;
    const offset = append ? this.nextImportOffset : 0;
    this.importsLoading = true;
    this.importError = null;
    if (!append) {
      this.importRows = [];
      this.nextImportOffset = null;
    }
    this.importsRequest = this.sourceService.list(this.projectId, {
      offset, limit: 50, search: this.importSearch.trim(),
    }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: result => {
        if (!this.currentScope(projectId, scopeGeneration, accountId) || generation !== this.importGeneration) return;
        this.importRows = append ? [...this.importRows, ...result.sources] : result.sources;
        this.nextImportOffset = result.nextOffset;
        this.importsLoading = false;
        if (this.focusImportId && this.importRows.some(row => row.id === this.focusImportId)) {
          this.selectedRow = null;
          this.selectedImportId = this.focusImportId;
          this.focusImportId = null;
          this.activeTab = 'review';
        }
        if (this.activeTab === 'review' && !this.showPendingTab) this.activeTab = 'map';
      },
      error: error => {
        if (!this.currentScope(projectId, scopeGeneration, accountId) || generation !== this.importGeneration) return;
        this.importsLoading = false;
        if ([401, 403].includes(error?.status)) {
          this.clearScope(); this.projectsService.closeRightPanel.next();
          this.importError = 'Your account no longer has access to these project layers.'; return;
        }
        this.importError = error.status === 503
          ? 'Layer uploads are not configured for this environment.'
          : 'Pending layers could not be loaded. Please try again.';
      },
    });
  }

  openImport(row: ProjectMapSourceSummary): void {
    if (!this.syncAccount() || !row.latestVersion?.id || !this.publishingService.canConfigure(this.projectId)) return;
    this.selectedRow = null;
    this.selectedImportId = row.id;
    this.projectsService.openRightPanel.next({
      component: CreateOrEditMapLayerComponent,
      data: { projectId: this.projectId, sourceVersionId: row.latestVersion.id, name: row.name,
        sourceFormat: row.kind === 'arcgis' || row.kind === 'placed_plan' ? undefined : row.kind as ProjectMapSourceFormat },
    });
  }

  create(groupId?: string): void {
    if (!this.syncAccount() || !this.publishingService.canConfigure(this.projectId)) return;
    this.selectedRow = null;
    this.selectedImportId = null;
    this.projectsService.openRightPanel.next({
      component: CreateOrEditMapLayerComponent,
      data: { projectId: this.projectId, groupId },
    });
  }

  rowClicked(row: ReferenceLayerSummary): void {
    if (!this.syncAccount() || !this.rows.some(item => item.id === row.id)) return;
    this.selectedImportId = null;
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({
      component: CreateOrEditMapLayerComponent,
      data: { ...row, projectId: this.projectId },
    });
  }

  /** Whether a pending import can be removed right now: the API refuses one that is still processing. */
  canRemoveImport(row: ProjectMapSourceSummary): boolean {
    const status = row.latestVersion?.status;
    return !!row.id && status !== 'published' && status !== 'inspecting' && status !== 'processing'
      && this.publishingService.canConfigure(this.projectId);
  }

  removeImport(row: ProjectMapSourceSummary, event?: Event): void {
    event?.stopPropagation();
    if (!this.syncAccount() || !this.canRemoveImport(row)) return;
    const projectId = this.projectId; const generation = this.projectGeneration; const accountId = this.accountId;
    const model = new ConfirmDialogModel(
      'Remove pending layer',
      `Remove "${row.name}"? The uploaded file and its review settings are discarded. Nothing on the map changes.`,
      'Remove',
      'Cancel',
    );
    this.dialog.open(ConfirmDialogComponent, { width: '400px', data: model })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((ok) => {
        if (!ok || !this.currentScope(projectId, generation, accountId)) return;
        this.sourceService.remove(projectId, row.id)
          .pipe(takeUntil(this.onDestroy))
          .subscribe({
            next: () => {
              if (!this.currentScope(projectId, generation, accountId)) return;
              if (this.selectedImportId === row.id) { this.selectedImportId = null; this.projectsService.closeRightPanel.next(); }
            },
            error: (error) => {
              if (!this.currentScope(projectId, generation, accountId)) return;
              this.importError = error?.status === 409 && error?.error?.error
                ? String(error.error.error)
                : 'The pending layer could not be removed. Please try again.';
            },
          });
      });
  }

  /** Withdraw a published layer: the map stops showing it, the source and its versions stay for a later re-add. */
  removeFromMap(row: ManagedMapLayer, event?: Event): void {
    event?.stopPropagation();
    const revision = this.catalog?.catalogRevision;
    if (!row.published || !this.syncAccount() || !this.publishingService.canPublish(this.projectId) || !Number.isInteger(revision)) return;
    const projectId = this.projectId; const generation = this.projectGeneration; const accountId = this.accountId;
    const model = new ConfirmDialogModel(
      'Remove layer from the map',
      `Remove "${row.name}" from the project map on web and mobile? The uploaded file is kept and the layer can be added again later.`,
      'Remove',
      'Cancel',
    );
    this.dialog.open(ConfirmDialogComponent, { width: '400px', data: model })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((ok) => {
        if (!ok || !this.currentScope(projectId, generation, accountId)) return;
        this.publishingService.withdraw(projectId, row.id, revision as number).then(async () => {
          if (!this.currentScope(projectId, generation, accountId)) return;
          if (this.selectedImportId && row.published?.sourceVersionId === this.selectedImportId) { this.selectedImportId = null; this.projectsService.closeRightPanel.next(); }
          // The withdrawn row is now flagged on the reference list too; reload it with the
          // catalog, or the layer lingers as an ungrouped legacy row with a dead menu.
          this.refLayerService.listChanged.next();
          await this.refreshCatalog();
        }).catch((error) => {
          if (!this.currentScope(projectId, generation, accountId)) return;
          this.catalogError = error?.status === 409 && error?.error?.error
            ? String(error.error.error)
            : 'The layer could not be removed from the map. Please try again.';
        });
      });
  }

  delete(row: ReferenceLayerSummary, event: Event): void {
    event?.stopPropagation();
    if (!this.syncAccount() || !this.publishingService.canConfigure(this.projectId)) return;
    const projectId = this.projectId; const generation = this.projectGeneration; const accountId = this.accountId;
    const model = new ConfirmDialogModel(
      'Remove reference layer',
      `Remove "${row.name}" from this project? The map will stop showing it.`,
      'Remove',
      'Cancel',
    );
    this.dialog.open(ConfirmDialogComponent, { width: '400px', data: model })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((ok) => {
        if (!ok || !this.currentScope(projectId, generation, accountId)) return;
        this.refLayerService.delete(projectId, row.id)
          .pipe(takeUntil(this.onDestroy))
          .subscribe(() => {
            if (!this.currentScope(projectId, generation, accountId)) return;
            this.refLayerService.listChanged.next();
            if (this.selectedRow?.id === row.id) {
              this.projectsService.closeRightPanel.next();
            }
          });
      });
  }

  get layerFolders(): { id: string; name: string; layers: ManagedMapLayer[] }[] {
    return [...(this.catalog?.groups || [])].sort((a, b) => a.order - b.order).map(group => ({
      ...group,
      layers: (this.catalog?.layers || []).filter(layer => layer.groupId === group.id)
        .sort((a, b) => a.order - b.order).map(published => ({ id: published.id, name: published.name, published })),
    }));
  }

  /** Legacy reference layers that are on the map without a catalog entry (withdrawn published layers are not on the map). */
  private get legacyRows(): ReferenceLayerSummary[] {
    const publishedIds = new Set(this.catalog?.layers.map(layer => layer.id) || []);
    return this.rows.filter(row => !publishedIds.has(row.id) && !row.map_withdrawn);
  }

  /** Published layers outside any known group, followed by legacy reference layers. */
  get unfiledLayers(): ManagedMapLayer[] {
    const groupIds = new Set(this.catalog?.groups.map(group => group.id) || []);
    const ungrouped = (this.catalog?.layers || []).filter(layer => !groupIds.has(layer.groupId))
      .sort((a, b) => a.order - b.order).map(published => ({ id: published.id, name: published.name, published }));
    return [...ungrouped, ...this.legacyRows.map(reference => ({ id: reference.id, name: reference.name, reference }))];
  }

  get onMapCount(): number { return (this.catalog?.layers.length || 0) + this.legacyRows.length; }

  trackLayer(_index: number, row: ManagedMapLayer): string { return row.id; }

  get pendingRows(): ProjectMapSourceSummary[] {
    return this.importRows.filter(row => row.latestVersion?.status !== 'published');
  }

  /** The Pending tab is hidden when nothing is pending; it stays while loading, erroring, paging, or searching. */
  get showPendingTab(): boolean {
    return this.pendingRows.length > 0 || this.importsLoading || !!this.importError || this.nextImportOffset !== null || !!this.importSearch.trim();
  }

  toggleFolder(id: string): void {
    if (this.collapsedFolders.has(id)) this.collapsedFolders.delete(id);
    else this.collapsedFolders.add(id);
  }

  async refreshCatalog(): Promise<void> {
    if (!this.syncAccount() || !this.projectId) return;
    this.catalogAbort.abort(); this.versionRequest?.unsubscribe();
    const controller = new AbortController(); this.catalogAbort = controller;
    const projectId = this.projectId; const generation = this.projectGeneration; const accountId = this.accountId;
    this.catalogLoading = true; this.catalogError = '';
    const current = () => !controller.signal.aborted && this.currentScope(projectId, generation, accountId);
    try {
      const catalog = await this.publishingService.catalog(projectId, controller.signal);
      if (!current()) return;
      this.catalog = catalog;
      this.sourceVersions = {};
      const ids = [...new Set(catalog?.layers.map(layer => layer.sourceVersionId) || [])];
      this.versionRequest = from(ids).pipe(
        mergeMap(id => this.sourceService.getVersion(projectId, id).pipe(
          map(version => ({ id, version })), catchError(() => of(null)),
        ), 4), takeUntil(this.onDestroy),
      ).subscribe(result => { if (result && current()) this.sourceVersions[result.id] = result.version; });
    } catch (error) {
      if (!current()) return;
      this.catalog = null;
      if ([401, 403].includes(error?.status)) {
        this.clearScope(); this.projectsService.closeRightPanel.next();
        this.catalogError = 'Your account no longer has access to these map layers.';
      } else this.catalogError = 'Design layers could not be loaded. Retry before making changes.';
    } finally { if (current()) this.catalogLoading = false; }
  }

  layerSource(row: ManagedMapLayer): string {
    if (row.reference) return this.sourceLabel(row.reference);
    if (this.fromDrawings(row)) return 'From Drawings';
    const version = this.sourceVersions[row.published.sourceVersionId];
    if (!version) return 'Source details unavailable';
    if (version.sourceKind === 'arcgis' || version.capture) return 'ArcGIS · captured copy';
    return version.filename || version.format?.toUpperCase() || 'Uploaded file';
  }

  /** A sheet placed from the Drawings tab, or a GeoPDF layer bound to a drawing revision. */
  fromDrawings(row: ManagedMapLayer): boolean {
    return !!row.published?.drawing || this.sourceVersions[row.published?.sourceVersionId]?.sourceKind === 'placed_plan';
  }

  /** Source column of a Pending row. */
  importSourceLabel(row: ProjectMapSourceSummary): string {
    return row.kind === 'placed_plan' ? 'From Drawings' : row.kind.toUpperCase();
  }

  /** The source a GeoPDF row could be registered as a drawing from, or null. */
  registrableSource(row: ManagedMapLayer | ProjectMapSourceSummary): string | null {
    if (!this.projectId || !this.ability.can('configure', subject('ProjectDrawing', { project_id: this.projectId }))
      || !this.publishingService.canConfigure(this.projectId)) return null;
    if ('kind' in row) return row.kind === 'pdf' ? row.id : null;
    const version = row.published && !row.published.drawing ? this.sourceVersions[row.published.sourceVersionId] : null;
    return version?.sourceId && (version.sourceKind === 'pdf' || version.format === 'pdf') ? version.sourceId : null;
  }

  /** Copies the GeoPDF into a draft issue set and opens its review in the Drawings tab. */
  registerAsDrawing(sourceId: string): void {
    if (!sourceId || this.registeringSourceId) return;
    this.registeringSourceId = sourceId;
    this.registerError = '';
    const projectId = this.projectId;
    this.drawingsApi.registerLayer(projectId, sourceId).pipe(takeUntil(this.onDestroy)).subscribe({
      next: set => {
        this.registeringSourceId = null;
        this.projectsService.closeRightPanel.next();
        void this.router.navigate(['/projects', projectId], { queryParams: { tab: 'drawings', subTab: null, set: set.uuid, sheet: null, revision: null },
          queryParamsHandling: 'merge' });
      },
      error: (e: { error?: { code?: string } }) => {
        this.registeringSourceId = null;
        this.registerError = this.drawingsApi.failureMessage(e?.error?.code, this.drawingsApi.errorMessage(e));
      },
    });
  }

  layerFeatureCount(row: ManagedMapLayer): number | string {
    if (row.published) {
      const version = this.sourceVersions[row.published.sourceVersionId];
      const sublayer = version?.sublayers.find(layer => layer.key === row.published.sublayerKey);
      return sublayer?.geometryTypes.includes('RASTER') ? 'Image' : sublayer?.featureCount ?? '—';
    }
    if (/\.(png|jpe?g|webp|tiff?)$/i.test(row.reference?.source_filename || '')) return 'Image';
    return row.reference?.source_type === 'arcgis_url' ? '—' : row.reference?.feature_count ?? '—';
  }

  openLayer(row: ManagedMapLayer, newVersion = false): void {
    if (!this.syncAccount() || !this.publishingService.canConfigure(this.projectId)) return;
    if (row.reference) { this.rowClicked(row.reference); return; }
    const layer = this.catalog?.layers.find(item => item.id === row.id);
    if (!layer) return;
    this.selectedRow = null; this.selectedImportId = layer.sourceVersionId;
    this.projectsService.openRightPanel.next({ component: CreateOrEditMapLayerComponent,
      data: { projectId: this.projectId, sourceVersionId: layer.sourceVersionId, name: layer.name,
        groupId: layer.groupId, layerId: layer.id, sourceCapturedAt: layer.sourceCapturedAt, newVersion: newVersion || undefined },
    });
  }

  /** Row menu: "Upload new version" (file sources) or "Capture new version" (ArcGIS copies), once the version is known. */
  newVersionLabel(row: ManagedMapLayer): string | null {
    const version = row.published ? this.sourceVersions[row.published.sourceVersionId] : null;
    // A placed drawing gets its next version from the Drawings tab (Place on map), never from a file.
    if (!version?.sourceId || version.status !== 'published' || version.sourceKind === 'placed_plan') return null;
    return version.sourceKind === 'arcgis' ? 'Capture new version…' : 'Upload new version…';
  }

  /** Open the layer's drawer straight into the new-version flow. */
  newVersion(row: ManagedMapLayer): void {
    if (!this.newVersionLabel(row)) return;
    this.openLayer(row, true);
  }

  /** Open the group editor: rename `group`, or create a new group that `layer` (from a row menu) moves into on save. */
  editFolder(group?: { id: string; name: string }, layer?: ManagedMapLayer): void {
    if (!this.syncAccount() || !this.publishingService.canPublish(this.projectId) || this.catalogLoading || this.catalogError || this.savingFolder) return;
    this.editingFolderId = group?.id || null;
    this.folderLayer = !group && layer?.published ? layer : null;
    this.folderName = group?.name || ''; this.folderError = '';
    const editingId = this.editingFolderId;
    const validate = (name: string): string | null => {
      if (editingId && !this.catalog?.groups.some(item => item.id === editingId)) return 'This group no longer exists. Close this dialog and choose another group.';
      if (this.catalog?.groups.some(item => item.id !== editingId && item.name.toLowerCase() === name.toLowerCase())) return 'A group with this name already exists.';
      return null;
    };
    this.dialog.open(MapLayerGroupDialogComponent, {
      width: '400px',
      data: {
        title: group ? 'Rename group' : 'New group',
        hint: this.folderLayer ? `"${this.folderLayer.name}" moves into this group when you create it.` : undefined,
        name: this.folderName, confirmLabel: group ? 'Save group' : 'Create group', validate,
      },
    }).afterClosed().pipe(takeUntil(this.onDestroy)).subscribe((name?: string) => {
      if (!name) return;
      this.folderName = name;
      void this.saveFolder();
    });
  }

  async saveFolder(): Promise<void> {
    const name = this.folderName.trim();
    if (this.editingFolderId && !this.catalog?.groups.some(group => group.id === this.editingFolderId)) {
      this.folderError = 'This group no longer exists. Close this form and choose another group.'; return;
    }
    if (!name || name.length > 256) { this.folderError = 'Enter a group name.'; return; }
    if (this.catalog?.groups.some(group => group.id !== this.editingFolderId && group.name.toLowerCase() === name.toLowerCase())) {
      this.folderError = 'A group with this name already exists.'; return;
    }
    const group = { id: this.editingFolderId || `folder-${Array.from(crypto.getRandomValues(new Uint8Array(8)), value => value.toString(16).padStart(2, '0')).join('')}`, name,
      order: this.catalog?.groups.find(item => item.id === this.editingFolderId)?.order
        ?? Math.max(0, ...(this.catalog?.groups.map(item => item.order + 1) || [])) };
    const publication = this.publishingService.publication(this.catalog, [], group);
    publication.groups = publication.groups.map(item => item.id === group.id ? group : item);
    const layer = this.editingFolderId ? null : this.folderLayer;
    if (layer && this.catalog?.layers.some(item => item.id === layer.id)) {
      publication.entries = this.assignGroup(publication.entries, layer.id, group.id);
    }
    await this.saveCatalog(publication);
  }

  /** Remove a group; its layers stay on the map, ungrouped. */
  removeFolder(group: { id: string; name: string }, event?: Event): void {
    event?.stopPropagation();
    if (!this.syncAccount() || !this.publishingService.canPublish(this.projectId) || this.catalogLoading || this.catalogError || this.savingFolder) return;
    if (!this.catalog?.groups.some(item => item.id === group.id)) return;
    const members = this.catalog.layers.filter(layer => layer.groupId === group.id).length;
    const projectId = this.projectId; const generation = this.projectGeneration; const accountId = this.accountId;
    const model = new ConfirmDialogModel(
      'Remove group',
      members
        ? `Remove "${group.name}"? Its ${members} ${members === 1 ? 'layer stays' : 'layers stay'} on the map, no longer grouped.`
        : `Remove the empty group "${group.name}"?`,
      'Remove',
      'Cancel',
    );
    this.dialog.open(ConfirmDialogComponent, { width: '400px', data: model })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe(async (ok) => {
        if (!ok || !this.currentScope(projectId, generation, accountId) || !this.catalog) return;
        const publication = this.publishingService.publication(this.catalog, [], null);
        publication.groups = publication.groups.filter(item => item.id !== group.id);
        publication.entries = publication.entries.map(entry => entry.groupId === group.id ? { ...entry, groupId: null } : entry);
        await this.saveCatalog(publication);
      });
  }

  /** True when the published layer sits in a group that still exists. */
  isGrouped(row: ManagedMapLayer): boolean {
    return !!row.published?.groupId && !!this.catalog?.groups.some(group => group.id === row.published.groupId);
  }

  /** Move a published layer into `groupId`, or out of every group when `groupId` is null. */
  async moveLayer(row: ManagedMapLayer, groupId: string | null): Promise<void> {
    const group = groupId === null ? null : this.catalog?.groups.find(item => item.id === groupId);
    if (!row.published || group === undefined || !this.catalog?.layers.some(item => item.id === row.id)) return;
    const publication = this.publishingService.publication(this.catalog, [], group);
    publication.entries = this.assignGroup(publication.entries, row.id, groupId);
    await this.saveCatalog(publication);
  }

  private assignGroup<T extends { id: string; groupId: string | null }>(entries: T[], layerId: string, groupId: string | null): T[] {
    return entries.map(entry => entry.id === layerId ? { ...entry, groupId } : entry);
  }

  private async saveCatalog(publication: ReturnType<ProjectMapPublishingService['publication']>): Promise<void> {
    if (!this.syncAccount() || !this.publishingService.canPublish(this.projectId) || this.catalogLoading || this.catalogError || this.savingFolder) return;
    const projectId = this.projectId; const generation = this.projectGeneration; const accountId = this.accountId;
    const signal = this.mutationAbort.signal;
    this.savingFolder = true; this.folderError = '';
    try {
      await this.publishingService.publish(projectId, publication, signal);
      if (signal.aborted || !this.currentScope(projectId, generation, accountId)) return;
      this.folderLayer = null;
      await this.refreshCatalog();
    } catch (error) {
      if (signal.aborted || !this.currentScope(projectId, generation, accountId)) return;
      if (error?.status === 409) {
        this.folderError = 'Someone updated these layers. The latest map has been loaded; check your change and save again.';
        await this.refreshCatalog();
      } else this.folderError = 'The group change could not be saved. Check your access and connection, then try again.';
    } finally { if (!signal.aborted && this.currentScope(projectId, generation, accountId)) this.savingFolder = false; }
  }

  sourceLabel(row: ReferenceLayerSummary): string {
    if (row.source_type === 'arcgis_url') return 'ArcGIS · live';
    return row.source_filename || 'GeoJSON upload';
  }
}
